/**
 * Resume Studio — Preview + payment flow (V1.4.5 refactor).
 *
 * Owns the preview screen, template picker, tier gate, Razorpay checkout,
 * plus the RevisionCard and revision top-up modal. Extracted from
 * ResumeStudioPage.jsx to keep the page file under 1600 lines.
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { fireGadsConversion } from "@/lib/gtagConversions";
import {
    ArrowRight, CheckCircle, Download, Lightning, LockKey,
    ShieldCheck, Sparkle, ArrowsClockwise, Warning,
} from "@phosphor-icons/react";
import ShareCVWidget from "./ShareCVWidget";
import StyleOptionsPanel from "./StyleOptionsPanel";
import RevisionCard from "./RevisionCard";
import ReferralShareCard from "./ReferralShareCard";
import { AtsScoreBadge, BackButton, loadRazorpay } from "./shared";
import { TIER_META } from "./constants";

// --- Preview + payment ---------------------------------------------------
function PreviewAndPay({ session, onSessionChange, pricing, onBack }) {
    // Feb 18, 2026 — Prod bug: opening a past PAID session via "Find your
    // past CVs" used to reset `tier` to "standard" and `templateId` to
    // "ats_minimal" on mount, then the on-mount `useEffect` immediately
    // called `POST /select-tier` and OVERWROTE the paid session's tier on
    // the server. When the user then clicked (say) "Elite Timeline" the
    // click was gated to standard-tier templates only, the iframe URL
    // changed but the doc still had the wrong template, and Chrome
    // rendered a blocked/failed-PDF icon. Init from the recovered session
    // when possible so we don't clobber saved state.
    const [templateId, setTemplateId] = useState(
        session.selected_template
        || (session.available_templates && session.available_templates[0])
        || "ats_minimal",
    );
    const [tier, setTier] = useState(
        session.tier
        || (session.mode === "fresher" ? "fresher" : "standard"),
    );
    const [templatesCatalog, setTemplatesCatalog] = useState([]);
    const [colorPresets, setColorPresets] = useState([]);
    const [busy, setBusy] = useState(false);
    const [previewLoading, setPreviewLoading] = useState(true);
    // Bug #2 (Feb 18, 2026): iframe + download need a cache-buster so
    // browsers don't serve stale disk-cached bytes across style toggles.
    // Bump ?s= whenever style options change so the iframe force-reloads
    // the freshly-rendered PDF instead of cached bytes.
    //
    // Follow-up fix (Feb 18, 2026 — user report): the earlier
    // `btoa(json).slice(0, 10)` only captured the JSON prefix, which is
    // IDENTICAL for `{"color":"cobalt",...}` vs `{"color":"purple",...}`
    // (both start with `{"color":"`), so Apply Style never changed the
    // URL and the iframe kept serving the old PDF. Use a djb2 hash over
    // the full string so ANY change flips the sig.
    const styleSig = JSON.stringify(session.style_options || {}) + JSON.stringify(session.fresher_input || {});
    const styleSigShort = (() => {
        let h = 5381;
        for (let i = 0; i < styleSig.length; i++) {
            h = ((h << 5) + h + styleSig.charCodeAt(i)) | 0;
        }
        // Unsigned 32-bit → base36 → short + collision-resistant across
        // realistic style_options mutations.
        return (h >>> 0).toString(36);
    })();
    const previewUrl = `${API}/resume-studio/session/${session.session_id}/preview.pdf?t=${templateId}&s=${styleSigShort}`;
    const finalPdfUrl = `${API}/resume-studio/session/${session.session_id}/final.pdf?t=${templateId}&s=${styleSigShort}`;
    const isPaid = session.payment_status === "paid";
    const originalAts = session.original_ats || {};
    const newAts = session.rewritten_ats || {};

    // Flash the spinner whenever the iframe URL changes (template swap,
    // style patch, photo upload etc.). Cleared by <iframe onLoad>.
    useEffect(() => {
        setPreviewLoading(true);
    }, [previewUrl, isPaid]);

    useEffect(() => {
        fetch(`${API}/resume-studio/templates`)
            .then((r) => r.json())
            .then((d) => {
                setTemplatesCatalog(d.templates || []);
                setColorPresets(d.color_presets || []);
            })
            .catch(() => {});
    }, []);

    const availableTemplates = pricing
        ? pricing.tiers.find((t) => t.id === tier)?.templates || []
        : [];

    const currentTierPrice = pricing?.tiers.find((t) => t.id === tier)?.price_inr;

    const selectTierAndTemplate = async (nextTier, nextTemplate) => {
        try {
            await fetch(`${API}/resume-studio/session/${session.session_id}/select-tier`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ tier: nextTier }),
            });
            await fetch(`${API}/resume-studio/session/${session.session_id}/select-template`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ template_id: nextTemplate }),
            });
        } catch (e) {
            // silent — user can retry via pay
        }
    };

    // On mount + when tier / template changes, sync server-side selection.
    // Feb 18, 2026 fix: skip the initial sync when the session ALREADY
    // holds the same tier + template (i.e. we hydrated from a recovered
    // paid session). Otherwise `POST /select-tier` overwrites the paid
    // tier back to the frontend default and the template picker gets
    // gated to a smaller set — which is what caused the "prohibited-icon
    // iframe" report on Feb 18, 2026.
    useEffect(() => {
        if (tier === session.tier && templateId === session.selected_template) {
            return;
        }
        selectTierAndTemplate(tier, templateId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tier, templateId]);

    const startPayment = async () => {
        setBusy(true);
        try {
            await selectTierAndTemplate(tier, templateId);
            const r = await fetch(`${API}/resume-studio/session/${session.session_id}/order`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ tier, kind: "tier" }),
            });
            const d = await r.json();
            if (!r.ok) throw new Error(d.detail || "Order failed");
            await loadRazorpay();
            const rzp = new window.Razorpay({
                key: d.key_id,
                amount: d.amount_paise,
                currency: d.currency,
                name: "NaukriDaddy Resume Studio",
                description: `${tier.toUpperCase()} plan · ${templateId}`,
                order_id: d.order_id,
                theme: { color: "#0b5bd6" },
                prefill: {
                    email: session.email,
                },
                handler: async (resp) => {
                    try {
                        const v = await fetch(
                            `${API}/resume-studio/session/${session.session_id}/verify`,
                            {
                                method: "POST",
                                headers: { "Content-Type": "application/json" },
                                body: JSON.stringify({
                                    razorpay_order_id: resp.razorpay_order_id,
                                    razorpay_payment_id: resp.razorpay_payment_id,
                                    razorpay_signature: resp.razorpay_signature,
                                }),
                            },
                        );
                        const vd = await v.json();
                        if (!v.ok) throw new Error(vd.detail || "Verify failed");
                        onSessionChange(vd.session);
                        // Google Ads conversion — value-based bidding
                        fireGadsConversion("resume_studio_purchase", {
                            value: currentTierPrice,
                            currency: "INR",
                            transaction_id: resp.razorpay_payment_id,
                        });
                        toast.success("Payment verified! Your CV is unlocked.");
                    } catch (e) {
                        toast.error(String(e.message).slice(0, 200));
                    }
                },
                modal: {
                    ondismiss: () => setBusy(false),
                },
            });
            rzp.open();
        } catch (e) {
            toast.error(String(e.message).slice(0, 200));
        } finally {
            setBusy(false);
        }
    };

    const download = () => {
        // Feb 18, 2026 — Bug #3/#5 fix: `?download=1` tells the backend to
        // send `Content-Disposition: attachment` so the browser downloads
        // the file. Without this flag the endpoint now returns `inline`
        // (iframe-friendly), which prevents the accidental "random resume
        // downloaded" every time the paid iframe reloads after a template
        // click.
        // Bug #2 fix: styleSig cache-buster ensures the download reflects
        // the latest compact/color/template selection (no stale disk-cached
        // PDF from an earlier style).
        const url = `${API}/resume-studio/session/${session.session_id}/final.pdf?download=1&t=${templateId}&s=${styleSigShort}`;
        window.open(url, "_blank");
    };

    return (
        <div className="max-w-6xl mx-auto">
            <BackButton onClick={onBack} />
            {/* Header */}
            <div className="flex flex-wrap items-center justify-between gap-3 mt-2">
                <div>
                    <h2 className="text-2xl font-bold">
                        {isPaid ? "Your CV is ready 🎉" : "Preview your new CV"}
                    </h2>
                    <div className="text-sm text-slate-500 mt-1">
                        {isPaid
                            ? "Download the full PDF below or ask AI for one more revision."
                            : "Pick a template + plan. Full PDF unlocks after payment."}
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    {originalAts.overall != null && (
                        <AtsScoreBadge score={originalAts.overall} band={originalAts.band} label="Before" muted />
                    )}
                    {newAts.overall != null && (
                        <>
                            <ArrowRight size={16} className="text-slate-400" />
                            <AtsScoreBadge score={newAts.overall} band={newAts.band} label="After AI" />
                        </>
                    )}
                    {session.has_photo && session.photo_quality_ok && !templateId.startsWith("ats_") && (
                        <span
                            data-testid="trust-badge-photo"
                            title="Uniform background · well-lit · clean framing"
                            className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 shadow-sm"
                        >
                            <CheckCircle weight="fill" size={12} className="text-emerald-600" />
                            Professional photo detected
                        </span>
                    )}
                    {session.has_photo && !session.photo_quality_ok && !templateId.startsWith("ats_") && (
                        <span
                            data-testid="trust-badge-photo-warn"
                            title="Photo will still appear on your CV, but a clean uniform background looks more professional."
                            className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200"
                        >
                            <Warning weight="fill" size={12} className="text-amber-600" />
                            Photo added · try a plain background
                        </span>
                    )}
                </div>
            </div>

            {/* V1.5.8 — post-payment referral share card. Only shows on paid state. */}
            {isPaid && session.email && (
                <div className="mt-6" data-testid="rs-referral-card-wrap">
                    <ReferralShareCard email={session.email} />
                </div>
            )}

            <div className="mt-6 grid lg:grid-cols-[1fr_360px] gap-6">
                {/* PDF preview with frontend blur overlay */}
                <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm">
                    {/* Prominent template picker bar (above the PDF) — shows real thumbnails
                        so users can see + compare before committing to a template. */}
                    <div className="border-b border-slate-200 bg-slate-50/60 px-4 py-3">
                        <div className="flex items-center justify-between gap-3 mb-2">
                            <div>
                                <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider">
                                    Choose your template
                                </div>
                                <div className="text-[11px] text-slate-500">
                                    {availableTemplates.length > 0
                                        ? `${availableTemplates.length} ${availableTemplates.length === 1 ? "template" : "templates"} unlocked on ${tier} tier — tap to preview`
                                        : "Pick a plan to unlock templates"}
                                </div>
                            </div>
                            {availableTemplates.length > 0 && (
                                <span className="text-[11px] font-semibold text-cobalt-700 bg-cobalt-50 px-2 py-1 rounded-full">
                                    Selected: {templatesCatalog.find((t) => t.id === templateId)?.name || templateId}
                                </span>
                            )}
                        </div>
                        <div
                            className="flex gap-3 overflow-x-auto pb-1 -mx-1 px-1"
                            data-testid="preview-template-strip"
                        >
                            {templatesCatalog
                                .filter((t) => availableTemplates.includes(t.id))
                                .map((t) => {
                                    const active = templateId === t.id;
                                    return (
                                        <button
                                            key={t.id}
                                            onClick={() => setTemplateId(t.id)}
                                            data-testid={`preview-template-thumb-${t.id}`}
                                            className={`flex-shrink-0 w-[112px] text-left rounded-lg border-2 transition-all bg-white overflow-hidden ${
                                                active
                                                    ? "border-cobalt-600 ring-2 ring-cobalt-500/30 shadow-md"
                                                    : "border-slate-200 hover:border-slate-400"
                                            }`}
                                        >
                                            <img
                                                src={`/resume-studio-samples/${t.id}-preview-1.png`}
                                                alt={t.name}
                                                loading="lazy"
                                                className="w-full h-[140px] object-cover object-top bg-slate-100"
                                                onError={(e) => {
                                                    // Fallback chain: preview-1 → final-1 → hide.
                                                    const img = e.currentTarget;
                                                    const finalUrl = `/resume-studio-samples/${t.id}-final-1.png`;
                                                    if (img.src.indexOf("-preview-1.png") !== -1) {
                                                        img.src = finalUrl;
                                                        return;
                                                    }
                                                    img.style.display = "none";
                                                }}
                                            />
                                            <div className="p-1.5 text-center">
                                                <div className="text-[11px] font-bold text-slate-800 leading-tight">
                                                    {t.name}
                                                </div>
                                                <div className="text-[9px] text-slate-500 uppercase tracking-wider">
                                                    {t.tier}
                                                </div>
                                            </div>
                                        </button>
                                    );
                                })}
                            {availableTemplates.length === 0 && (
                                <div className="text-xs text-slate-400 py-3 italic">
                                    Templates unlock after you pick a plan →
                                </div>
                            )}
                        </div>
                        {/* ARIA live announcement — screen readers hear the
                            selected template + tier whenever the user swaps.
                            Visually hidden via `sr-only`. */}
                        <div
                            role="status"
                            aria-live="polite"
                            aria-atomic="true"
                            className="sr-only"
                            data-testid="preview-template-live-region"
                        >
                            {(() => {
                                const sel = templatesCatalog.find((t) => t.id === templateId);
                                if (!sel) return "";
                                return `Selected ${sel.name} template on ${sel.tier} tier`;
                            })()}
                        </div>
                    </div>
                    {/* Paper-desk background — mobile Chrome renders inline
                        PDFs edge-to-edge in an iframe, hiding the page border.
                        Wrapping the iframe in a subtle grey shell with soft
                        inner shadow makes the PDF feel like it sits on a
                        desk, so the built-in A4 margins are visible. */}
                    <div
                        className="relative bg-slate-100 p-2 md:p-3"
                        style={{ height: 780 }}
                    >
                        {/* Live-reload spinner — flashes while iframe fetches
                            the fresh preview after a template/style/photo change.
                            Cleared on iframe load event. */}
                        {previewLoading && (
                            <div
                                data-testid="preview-loading-overlay"
                                className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 backdrop-blur-sm pointer-events-none"
                            >
                                <div className="inline-flex items-center gap-2 bg-white/95 border border-slate-200 rounded-full px-4 py-2 shadow-lg">
                                    <ArrowsClockwise weight="bold" size={14} className="text-cobalt-600 animate-spin" />
                                    <span className="text-xs font-semibold text-slate-700">Updating preview…</span>
                                </div>
                            </div>
                        )}
                        <iframe
                            key={previewUrl + (isPaid ? "-paid" : "")}
                            src={isPaid ? finalPdfUrl : previewUrl}
                            title="Resume preview"
                            className="w-full h-full bg-white shadow-md rounded-sm"
                            data-testid="resume-preview-iframe"
                            onLoad={() => setPreviewLoading(false)}
                        />
                        {!isPaid && (
                            <>
                                {/* Bottom-half blur overlay + unlock CTA */}
                                <div
                                    className="pointer-events-none absolute inset-x-0 bottom-0"
                                    style={{
                                        top: "45%",
                                        backdropFilter: "blur(6px)",
                                        WebkitBackdropFilter: "blur(6px)",
                                        background:
                                            "linear-gradient(to bottom, rgba(255,255,255,0.20) 0%, rgba(255,255,255,0.80) 60%, rgba(255,255,255,0.98) 100%)",
                                    }}
                                />
                                <div className="pointer-events-none absolute inset-x-0 bottom-4 flex items-center justify-center">
                                    <div className="pointer-events-none inline-flex flex-col items-center bg-white/80 backdrop-blur border border-slate-200 rounded-2xl px-4 py-3 shadow-md">
                                        <span className="inline-flex items-center gap-2 text-cobalt-700 font-bold text-sm">
                                            <LockKey weight="fill" size={16} /> Full PDF locked
                                        </span>
                                        <span className="text-xs text-slate-500 mt-0.5">
                                            Pay ₹{currentTierPrice} to remove blur + watermark
                                        </span>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* Sidebar: tier + template + pay */}
                <aside className="space-y-4">
                    <div className="rounded-2xl border border-slate-200 bg-white p-5">
                        <h3 className="font-bold">Choose your plan</h3>
                        <div className="mt-3 space-y-2">
                            {(pricing?.tiers || []).map((t) => {
                                const meta = TIER_META[t.id] || {};
                                const active = tier === t.id;
                                const isPurpleTier = t.id === "pro";
                                const isAmberTier = t.id === "elite";
                                const revBadgeCls = t.revisions === 0
                                    ? "bg-slate-100 text-slate-500"
                                    : isAmberTier
                                        ? "bg-amber-100 text-amber-800"
                                        : isPurpleTier
                                            ? "bg-purple-100 text-purple-800"
                                            : "bg-emerald-100 text-emerald-800";
                                return (
                                    <button
                                        key={t.id}
                                        onClick={() => {
                                            setTier(t.id);
                                            // reset template if not available in new tier
                                            if (!t.templates.includes(templateId)) {
                                                setTemplateId(t.templates[0]);
                                            }
                                        }}
                                        data-testid={`preview-tier-${t.id}-btn`}
                                        className={`w-full text-left border rounded-xl p-3 transition-all ${
                                            active
                                                ? "border-cobalt-500 bg-cobalt-50/50 ring-2 ring-cobalt-500/30"
                                                : "border-slate-200 hover:border-slate-300"
                                        }`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <div className="font-bold text-sm">{meta.label || t.id}</div>
                                            <div className="font-black text-base">₹{t.price_inr}</div>
                                        </div>
                                        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 uppercase tracking-wide">
                                                {t.templates.length} {t.templates.length > 1 ? "templates" : "template"}
                                            </span>
                                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide ${revBadgeCls}`}>
                                                {t.revisions === 0
                                                    ? "no revisions"
                                                    : `${t.revisions} AI ${t.revisions === 1 ? "revision" : "revisions"}`}
                                            </span>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                        <p className="mt-3 text-[11px] text-slate-500 leading-relaxed">
                            AI revisions let you tweak the CV after generation ("emphasize Python", "shorten summary"). Ran out? Top up ₹599 for 10 more.
                        </p>
                    </div>

                    <div className="rounded-2xl border border-slate-200 bg-white p-5">
                        <h3 className="font-bold">Template</h3>
                        <div className="mt-3 grid gap-2">
                            {templatesCatalog
                                .filter((t) => availableTemplates.includes(t.id))
                                .map((t) => {
                                    const active = templateId === t.id;
                                    return (
                                        <button
                                            key={t.id}
                                            onClick={() => setTemplateId(t.id)}
                                            data-testid={`preview-template-${t.id}-btn`}
                                            className={`w-full text-left border rounded-lg p-2.5 transition-all ${
                                                active
                                                    ? "border-cobalt-500 bg-cobalt-50/40"
                                                    : "border-slate-200 hover:border-slate-300"
                                            }`}
                                        >
                                            <div className="font-semibold text-sm">{t.name}</div>
                                            <div className="text-xs text-slate-500">{t.blurb}</div>
                                        </button>
                                    );
                                })}
                            {availableTemplates.length === 0 && (
                                <div className="text-xs text-slate-400">
                                    Templates unlock after you pick a plan.
                                </div>
                            )}
                        </div>
                    </div>

                    {!isPaid ? (
                        <button
                            onClick={startPayment}
                            disabled={busy}
                            data-testid="preview-pay-btn"
                            className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-cobalt-600 to-purple-600 text-white font-bold px-5 py-3 rounded-xl shadow-md hover:shadow-lg disabled:opacity-40"
                        >
                            {busy ? "Opening Razorpay…" : `Unlock for ₹${currentTierPrice}`}
                        </button>
                    ) : (
                        <div className="space-y-2">
                            <button
                                onClick={download}
                                data-testid="preview-download-btn"
                                className="w-full inline-flex items-center justify-center gap-2 bg-emerald-600 text-white font-bold px-5 py-3 rounded-xl hover:bg-emerald-700"
                            >
                                <Download size={16} weight="bold" /> Download final PDF
                            </button>
                            <RevisionCard session={session} onSessionChange={onSessionChange} />
                            <StyleOptionsPanel
                                session={session}
                                onSessionChange={onSessionChange}
                                colorPresets={colorPresets}
                            />
                            <ShareCVWidget session={session} />
                        </div>
                    )}
                    {!isPaid && (
                        <StyleOptionsPanel
                            session={session}
                            onSessionChange={onSessionChange}
                            colorPresets={colorPresets}
                        />
                    )}

                    <div className="text-xs text-slate-400 text-center">
                        Secure Razorpay checkout · UPI, cards, wallets
                    </div>
                </aside>
            </div>
        </div>
    );
}

export default PreviewAndPay;
